import React from 'react';
import './ScopesPanel.css';

// Granted-OAuth-scopes panel — used by both the user-detail view
// (Admin → Users → {id}) and the social-accounts detail view
// (Admin → Social Accounts → {id}). Renders the result of the
// /admin/users/{user_id}/social-scopes endpoint for a single platform:
// granted vs missing scopes, token type, validity, expiry, and the
// granular per-asset target_ids when present.
//
// Props:
//   - platform: 'instagram' | 'facebook_page' | 'tiktok' (used only for
//     a small contextual hint at the bottom)
//   - info: the platforms[platform] object from the backend response,
//     i.e. { connected, scopes, missing_scopes, expected_scopes,
//     granular_scopes, token_type, is_valid, expires_at, error, ... }
function ScopesPanel({ platform, info }) {
  if (!info) return null;

  if (!info.connected) {
    return (
      <div className="scopes-panel scopes-panel-disconnected">
        <div className="scopes-panel-row">
          <span className="scopes-panel-label">Status</span>
          <span className="scopes-panel-value">Not connected ({info.reason})</span>
        </div>
      </div>
    );
  }

  const granted = info.scopes || [];
  const missing = info.missing_scopes || [];
  const expected = info.expected_scopes || [];
  const extras = granted.filter((s) => !expected.includes(s));
  const granular = info.granular_scopes || [];

  const formatExpiry = (v) => {
    if (!v) return null;
    if (typeof v === 'number') {
      // Unix timestamp from Meta debug_token
      try {
        return new Date(v * 1000).toLocaleString();
      } catch {
        return String(v);
      }
    }
    try {
      return new Date(v).toLocaleString();
    } catch {
      return String(v);
    }
  };

  return (
    <div className="scopes-panel">
      <div className="scopes-panel-header">
        <span className="scopes-panel-title">🔐 Granted permissions</span>
        {info.token_type && (
          <span className="scopes-panel-token-type">type: {info.token_type}</span>
        )}
      </div>

      {info.error && (
        <div className="scopes-panel-error">⚠️ {String(info.error).slice(0, 240)}</div>
      )}

      {info.is_valid != null && (
        <div className="scopes-panel-row">
          <span className="scopes-panel-label">Token valid</span>
          <span className={info.is_valid ? 'scopes-pill scopes-pill-ok' : 'scopes-pill scopes-pill-bad'}>
            {info.is_valid ? '✓ Yes' : '✗ No'}
          </span>
        </div>
      )}

      {info.expires_at && (
        <div className="scopes-panel-row">
          <span className="scopes-panel-label">Expires</span>
          <span className="scopes-panel-value">{formatExpiry(info.expires_at)}</span>
        </div>
      )}

      {missing.length > 0 && (
        <div className="scopes-panel-block scopes-panel-block-missing">
          <div className="scopes-panel-block-title">
            ✗ Missing ({missing.length}/{expected.length})
          </div>
          <div className="scopes-panel-tags">
            {missing.map((s) => (
              <span key={s} className="scope-tag scope-tag-missing">{s}</span>
            ))}
          </div>
          <div className="scopes-panel-hint">
            Reconnect the account from the user side, granting all permissions
            in the OAuth popup. If a permission still doesn't appear in the
            popup, App Review is required for that scope.
          </div>
        </div>
      )}

      {granted.length > 0 && (
        <div className="scopes-panel-block">
          <div className="scopes-panel-block-title">
            ✓ Granted ({granted.length})
          </div>
          <div className="scopes-panel-tags">
            {granted.map((s) => {
              const isExpected = expected.includes(s);
              return (
                <span
                  key={s}
                  className={`scope-tag ${isExpected ? 'scope-tag-ok' : 'scope-tag-extra'}`}
                  title={isExpected ? 'Expected scope' : 'Extra scope (not in expected set)'}
                >
                  {s}
                </span>
              );
            })}
          </div>
          {extras.length > 0 && (
            <div className="scopes-panel-hint">
              Extra scopes (not strictly required by the publish pipeline):
              {' '}{extras.join(', ')}
            </div>
          )}
        </div>
      )}

      {granular.length > 0 && (
        <details className="scopes-panel-block scopes-panel-granular">
          <summary>Granular scopes ({granular.length}) — per-asset target_ids</summary>
          <div className="scopes-granular-list">
            {granular.map((g, i) => (
              <div key={`${g.scope}-${i}`} className="scopes-granular-row">
                <span className="scopes-granular-scope">{g.scope}</span>
                {Array.isArray(g.target_ids) && g.target_ids.length > 0 && (
                  <span className="scopes-granular-targets">
                    target_ids: {g.target_ids.join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </details>
      )}

      {platform === 'tiktok' && (
        <div className="scopes-panel-hint">
          TikTok scopes come from the OAuth response stored at connect
          time. There is no public token-introspection endpoint, so the
          list reflects what TikTok returned the day the user connected.
        </div>
      )}
    </div>
  );
}

export default ScopesPanel;
